import { useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import useHelpers from "Hooks/useHelpers";
import {
    IMatchTool,
    IMatchData,
    ISelection,
} from "Pages/Tools/MatchTools/MatchToolsSetup";
import styles from "Pages/Tools/MatchTools/MatchTools.module.css";
import DOMPurify from "dompurify";
import { normalizeSystemKey, resolveSystemIcon, useIsDarkTheme } from "Pages/Integration/systemIcons";
import { useParams } from "react-router-dom";

interface IProps {
    readonly matchTool: IMatchTool;
    readonly matchToolData: IMatchData;
    readonly getMatchToolData: (url: string) => void;
    readonly onSetMatchToolData: (data: IMatchData) => void;
    readonly onSetModal: (modal: boolean) => void;
    readonly selection: ISelection | undefined;
    readonly system1: string;
    readonly system2: string;
}

export default function MatchTools({
    matchTool,
    matchToolData,
    getMatchToolData,
    onSetMatchToolData,
    onSetModal,
    selection,
    system1,
    system2,
}: IProps) {
    // Procore is system 1
    const [system1Selection, setSystem1Selection] = useState<any>();
    const [system2Selection, setSystem2Selection] = useState<any>();
    const [showHiddenColumns, setShowHiddenColumns] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);

    const { newAlert } = useAlertContext();
    const { post } = useFetch();
    const { formatIsoStrings } = useHelpers();
    const { tool } = useParams();

    const onSetSystem1Selection = (row: any) => {
        setSystem1Selection(row);
    };

    const onSetSystem2Selection = (row: any) => {
        setSystem2Selection(row);
    };

    /*
     * Preview mode applies to EVERY action button. It appends /DEBUG to the queued job, which the
     * Runner runs in preview: the provider walks every guard and logs what it would create, update
     * or link, but the connectors' write wrappers write nothing (same suffix as the Run page's
     * preview, IntegrationRow.tsx). So a preview changes no row here either: no optimistic update,
     * and the selection is kept so the user can preview and then run the same action for real.
     */
    const jobQueueUrl = (method: string, args: string) =>
        `/api/jobqueue/${method}/${args}${previewMode ? "/DEBUG" : ""}`;

    const alertPreviewSubmitted = (action: string) =>
        newAlert({
            message: `${action} preview submitted. Nothing was changed - see the Preview Mode run in Runs for what it would do.`,
            color: "success",
        });

    const linkRecords = async () => {
        // Require two rows selected for link / undefined check
        if (!system1Selection || !system2Selection) {
            newAlert({
                message: "Please select two rows to link.",
                color: "danger",
            });
            return;
        }

        if (
            system1Selection.values.integrated.toLowerCase() === "yes" &&
            system2Selection.values.integrated.toLowerCase() === "yes"
        ) {
            newAlert({
                message:
                    "Both records must be unlinked and not marked as 'Yes' in the 'Integrated' column first to link.",
                color: "danger",
            });
            return;
        }

        // Get method, parameters from matchTool json
        const action = matchTool.actions.find(
            (a: any) => a.method_type === "link"
        );

        if (!action) {
            newAlert({ message: "Error linking records" });
            console.log("Link method info not found in match tools data");
            return;
        }

        const method = action.method_name;

        const args = getArgsForSelectedRowFromActionParameters(action);

        const url = jobQueueUrl(method, args);
        const response = await post(url);

        console.log(`Executing ${url}...`);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Link response: ", data);

        if (response.ok && previewMode) {
            alertPreviewSubmitted("Link");
        } else if (response.ok) {
            updateStateForLinkOptimistically();

            // Deselect the selected table rows
            setSystem1Selection(null);
            setSystem2Selection(null);

            // Display alert
            newAlert({ message: "Link request submitted", color: "success" });
        } else {
            newAlert({ message: "Link request failed", color: "danger" });
        }
    };

    /** Returns string of method args separated by slashes */
    function getArgsForSelectedRowFromActionParameters(action: {
        method_type: string;
        method_name: string;
        method_parameters: { system: string; parameters: string[] }[];
    }): string {
        const parameter_definitions = action.method_parameters;

        const args = parameter_definitions
            .map((pd) => {
                const selection =
                    pd.system.trim() === matchToolData.data_set_1.name.trim()
                        ? system1Selection
                        : system2Selection;
                return (
                    pd.parameters
                        // Use 茶 instead of / to prevent slashes within an arg from
                        // later being separated into multiple args
                        .map((p) => {
                            try {
                                // Read from .original (full row), NOT .values — react-table's
                                // row.values only carries the *visible* columns, so params that
                                // live in hidden_columns (project_id/prime_id/item_id, etc.)
                                // come back undefined and the arg silently goes blank.
                                const value = selection.original[p];
                                console.log(`System: ${pd.system}, Param: ${p}, Value:`, value);
                                return value.toString().replace("/", "茶")
                            } catch (error) { // Catch error if property does not exist
                                console.error(`Error processing match param ${p} for system ${pd.system}:`, error);
                                return "";
                            }
                        })
                        .join("/")
                );
            })
            .join("/");
        return args;
    }

    /** Helper function for linkOrUnlink. Optimistically updates matchToolData state by
     * adding/removing the item_id of one to the match_ids of the other and vice versa */
    function updateStateForLinkOptimistically() {
        const newData = { ...matchToolData };
        let newSystem1Selection = { ...system1Selection.original };
        let newSystem2Selection = { ...system2Selection.original };

        // Add the match ids to the other row
        newSystem1Selection.match_ids = [
            ...system1Selection.original.match_ids,
            system2Selection.original.item_id,
        ];
        newSystem2Selection.match_ids = [
            ...system2Selection.original.match_ids,
            system1Selection.original.item_id,
        ];

        if ("integrated" in newSystem1Selection) {
            newSystem1Selection.integrated =
                typeof newSystem1Selection.integrated === "boolean"
                    ? true
                    : "Yes";
        }
        if ("integrated" in newSystem2Selection) {
            newSystem2Selection.integrated =
                typeof newSystem2Selection.integrated === "boolean"
                    ? true
                    : "Yes";
        }

        // Update each system
        const newSystem1Data = newData.data_set_1.data.map((v: any) => {
            if (v.item_id === newSystem1Selection.item_id) {
                return newSystem1Selection;
            }
            return v;
        });

        const newSystem2Data: any = newData.data_set_2.data.map((v: any) => {
            if (v.item_id === newSystem2Selection.item_id) {
                return newSystem2Selection;
            }
            return v;
        });

        // Update each system's data
        newData.data_set_1.data = newSystem1Data;
        newData.data_set_2.data = newSystem2Data;

        onSetMatchToolData(newData);
    }

    /*
     * A config-gated action the client has not switched on. On Match Projects and Match
     * Companies/Vendors the portal sends it marked disabled instead of dropping it
     * (Controllers/Tools/MatchTools.RemoveDisabledActions), so the button shows greyed out with
     * the reason. Returns that reason, or undefined when the action is usable.
     */
    function gatedOffReason(type: string): string | undefined {
        const action = matchTool.actions.find((a: any) => a.method_type === type);
        if (!action?.disabled) {
            return undefined;
        }
        return action.disabled_reason || "Available - contact Calance to enable";
    }

    /*
     * Send: push ONE record from system 1 into system 2, creating it there.
     *
     * Unlike link/unlink/ignore this does not act on a PAIRING — there is nothing to pair with yet,
     * which is the whole point. So it takes only the system-1 row, and it is deliberately refused
     * when a system-2 row is also selected: that combination almost always means the user meant to
     * Link an existing record rather than create a second one.
     *
     * The button is disabled unless canSend holds; the guards below repeat those conditions because
     * the handler must not depend on the button being the only way in.
     */
    const sendGatedReason = gatedOffReason("send");

    const canSend = Boolean(
        !sendGatedReason &&
        system1Selection &&
        !system2Selection &&
        (system1Selection.values?.integrated ?? "").toLowerCase() === "no"
    );

    const sendRecord = async () => {
        if (sendGatedReason) {
            newAlert({ message: sendGatedReason, color: "danger" });
            return;
        }

        if (!system1Selection) {
            newAlert({
                message: `Please select a single ${matchToolData?.data_set_1?.name ?? "Procore"} record to send.`,
                color: "danger",
            });
            return;
        }

        if (system2Selection) {
            newAlert({
                message:
                    "Send creates a new record in the other system, so it takes only the " +
                    `${matchToolData?.data_set_1?.name ?? "Procore"} row. If the record already exists, use Link instead.`,
                color: "danger",
            });
            return;
        }

        if ((system1Selection.values?.integrated ?? "").toLowerCase() !== "no") {
            newAlert({
                message:
                    "This record is already integrated, so sending it would create a duplicate. " +
                    "Only records marked 'No' in the Integrated column can be sent.",
                color: "danger",
            });
            return;
        }

        const action = matchTool.actions.find(
            (a: any) => a.method_type === "send"
        );
        if (!action) {
            newAlert({ message: "Error sending record" });
            console.log("Send method info not found in match tools data");
            return;
        }

        const method = action.method_name;
        const args = getArgsForSelectedRowFromActionParameters(action);

        const url = jobQueueUrl(method, args);
        console.log(`Executing ${url}...`);

        const response = await post(url);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Send response: ", data);

        if (!response.ok) {
            newAlert({ message: "Send request failed", color: "danger" });
            return;
        }

        if (previewMode) {
            alertPreviewSubmitted("Send");
            return;
        }

        // No optimistic row update: the record does not exist in the other system until the
        // queued job runs, and inventing a row here would show a match that is not there yet.
        setSystem1Selection(null);
        setSystem2Selection(null);
        newAlert({
            message: "Send request submitted. Refresh once the job has run to see the new record.",
            color: "success",
        });
    };

    /*
     * Get: send's MIRROR — pull ONE record from system 2 into system 1, creating it there.
     *
     * Same "nothing to pair with yet" shape as Send, opposite side, so every guard is mirrored:
     * it takes only the system-2 row, and it is refused when a system-1 row is also selected,
     * because that combination means the user meant to Link an existing record rather than
     * create a second copy of it.
     *
     * The integrated flag is read off .original rather than .values: react-table's row.values
     * carries only the VISIBLE columns, so a tool that puts "integrated" in hidden_columns would
     * make this read undefined and silently disable the button. (.values is kept as a fallback so
     * behaviour is unchanged for tools that render it.)
     */
    const system2Integrated = (
        system2Selection?.original?.integrated ??
        system2Selection?.values?.integrated ??
        ""
    ).toString();

    const getGatedReason = gatedOffReason("get");

    const canGet = Boolean(
        !getGatedReason &&
        system2Selection &&
        !system1Selection &&
        system2Integrated.toLowerCase() === "no"
    );

    // How-to text for Send and Get, shown whether or not the button is enabled. A gated-off
    // action shows its gate reason instead.
    const system1Label = matchToolData?.data_set_1?.name ?? "Procore";
    const system2Label = matchToolData?.data_set_2?.name ?? "ERP";
    const isDarkTheme = useIsDarkTheme();
    const system1Icon = resolveSystemIcon(normalizeSystemKey(system1Label), isDarkTheme);
    const system2Icon = resolveSystemIcon(normalizeSystemKey(system2Label), isDarkTheme);
    const sendTooltip =
        sendGatedReason ??
        `Creates the selected ${system1Label} record in ${system2Label}. ` +
            `Select one ${system1Label} row whose Integrated column is No, and no ${system2Label} row. ` +
            `To pair two records that already exist, use Link.`;
    const getTooltip =
        getGatedReason ??
        `Creates the selected ${system2Label} record in ${system1Label}. ` +
            `Select one ${system2Label} row whose Integrated column is No, and no ${system1Label} row. ` +
            `To pair two records that already exist, use Link.`;

    const getRecord = async () => {
        const system2Name = matchToolData?.data_set_2?.name ?? "ERP";
        const system1Name = matchToolData?.data_set_1?.name ?? "Procore";

        if (getGatedReason) {
            newAlert({ message: getGatedReason, color: "danger" });
            return;
        }

        if (!system2Selection) {
            newAlert({
                message: `Please select a single ${system2Name} record to get.`,
                color: "danger",
            });
            return;
        }

        if (system1Selection) {
            newAlert({
                message:
                    `Get creates a new record in ${system1Name}, so it takes only the ` +
                    `${system2Name} row. If the record already exists, use Link instead.`,
                color: "danger",
            });
            return;
        }

        if (system2Integrated.toLowerCase() !== "no") {
            newAlert({
                message:
                    "This record is already integrated, so getting it would create a duplicate. " +
                    "Only records marked 'No' in the Integrated column can be retrieved.",
                color: "danger",
            });
            return;
        }

        const action = matchTool.actions.find(
            (a: any) => a.method_type === "get"
        );
        if (!action) {
            newAlert({ message: "Error getting record" });
            console.log("Get method info not found in match tools data");
            return;
        }

        const method = action.method_name;
        const args = getArgsForSelectedRowFromActionParameters(action);

        const url = jobQueueUrl(method, args);
        console.log(`Executing ${url}...`);

        const response = await post(url);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Get response: ", data);

        if (response.ok && previewMode) {
            alertPreviewSubmitted("Get");
        } else if (response.ok) {
            // No optimistic row update, for the same reason Send does not: the record does not
            // exist on the other side until the queued job runs.
            setSystem1Selection(null);
            setSystem2Selection(null);
            newAlert({
                message: "Get request submitted. Refresh once the job has run to see the new record.",
                color: "success",
            });
        } else {
            newAlert({ message: "Get request failed", color: "danger" });
        }
    };

    const unlinkRecord = async () => {
        if (!system1Selection) {
            newAlert({
                message: "Please select a Procore record to unlink.",
                color: "danger",
            });
            return;
        }

        // if yes or matched lost then allow unlinking
        if (system1Selection.values.integrated.toLowerCase() === "no")
        {
            newAlert({
                message:
                    "Procore record is not linked, and cannot be unlinked.",
                color: "danger",
            });
            return;
        }

        // Copy row data object (and get null if not selected)
        let newSystem1Selection = system1Selection && {
            ...system1Selection.original,
        };
        let newSystem2Selection = system2Selection && {
            ...system2Selection.original,
        };

        // Find the corresponding rows whose item_id is in the match_ids of the other row
        if (!newSystem1Selection) {
            newSystem1Selection = matchToolData.data_set_1.data.find((row) =>
                newSystem2Selection.match_ids && newSystem2Selection.match_ids.includes(row.item_id)
            );
        }
        if (!newSystem2Selection) {
            newSystem2Selection = matchToolData.data_set_2.data.find((row) =>
                newSystem1Selection.match_ids && newSystem1Selection.match_ids.includes(row.item_id)
            );
        }

        // Get method, parameters from matchTool json
        const action = matchTool.actions.find(
            (a: any) => a.method_type === "unlink"
        );
        if (!action) {
            newAlert({ message: "Error unlinking records" });
            console.log("Unlink method info not found in match tools data");
            return;
        }
        const method = action.method_name;
        const args = getArgsForSelectedRowFromActionParameters(action);

        // Network request to endpoint that adds job to the job queue table
        const url = jobQueueUrl(method, args);

        console.log(`Executing ${url}...`);

        const response = await post(url);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Unlink response: ", data);

        if (response.ok && previewMode) {
            alertPreviewSubmitted("Unlink");
        } else if (response.ok) {
            updateStateForUnlinkOptimistically(
                newSystem1Selection,
                newSystem2Selection
            );

            // Deselect the selected table rows
            setSystem1Selection(null);
            setSystem2Selection(null);

            // Display alert
            newAlert({ message: "Unlink request submitted", color: "success" });
        } else {
            newAlert({ message: "Unlink request failed", color: "danger" });
        }
    };

    function updateStateForUnlinkOptimistically(
        newSystem1Selection: any,
        newSystem2Selection: any
    ) {
        const newData = { ...matchToolData };

        // Filter out the relevant item_ids from the match_ids of each selection.
        // Both sides are guarded together: newSystem2Selection is undefined whenever no
        // counterpart row could be resolved from the loaded data - nothing selected on the ERP
        // side and no data_set_2 row matching this row's match_ids (stale ids, or a counterpart
        // outside the loaded page). With no counterpart there is nothing to filter on either side.
        if (newSystem2Selection) {
            newSystem1Selection.match_ids = [
                ...newSystem1Selection.match_ids.filter(
                    (id: string) => id !== newSystem2Selection.item_id
                ),
            ];

            newSystem2Selection.match_ids = [
                ...newSystem2Selection.match_ids.filter(
                    (id: string) => id !== newSystem1Selection.item_id
                ),
            ];
        }

        // If there is an integrated property, set it to false if the value is a boolean
        // or set it to "" if the value is a string
        if ("integrated" in newSystem1Selection) {
            newSystem1Selection.integrated =
                typeof newSystem1Selection.integrated === "boolean"
                    ? false
                    : "No";
        }
        if (newSystem2Selection && "integrated" in newSystem2Selection) {
            newSystem2Selection.integrated =
                typeof newSystem2Selection.integrated === "boolean"
                    ? false
                    : "No";
        }

        // Use the new match_ids to update the data arrays of each system
        const newSystem1Data = newData.data_set_1.data.map((v: any) => {
            if (v.item_id === newSystem1Selection.item_id) {
                return newSystem1Selection;
            }
            return v;
        });

        const newSystem2Data = newSystem2Selection
            ? newData.data_set_2.data.map((v: any) => {
                  if (v.item_id === newSystem2Selection.item_id) {
                      return newSystem2Selection;
                  }
                  return v;
              })
            : null;

        // Update the copy of matchToolData (newData) with the new data properties (arrays)
        // Conditionals were added for newSystem2Selection possibly being null, if someone
        // clicked to unlink a Procore record that did not have a match in the other system
        newData.data_set_1.data = newSystem1Data;
        if (newSystem2Data) {
            newData.data_set_2.data = newSystem2Data;
        }

        // Finally update state with the new value
        onSetMatchToolData(newData);
    }

    const handleIgnoreButtonClick = async () => {
        if (!system1Selection) {
            newAlert({
                message: "Please select a Procore record to ignore.",
            });
            return;
        }

        // .original (full row) — match_ids/origin_id are hidden_columns, so
        // .values would drop them and silently skip the "unlink first" guard.
        const row = system1Selection.original;

        if (row.match_ids && row.match_ids.length && !row.origin_id?.startsWith("IGNORE")) {
            newAlert({
                message: "Please unlink the record before ignoring.",
            });
            return;
        }

        if (row.integrated === "Ignore") {
            await unlinkRecord();
        } else {
            await ignoreRecord(system1Selection);
        }
    };

    async function ignoreRecord(system1Selection: any) {
        if (!system1Selection) {
            newAlert({
                message: "Please select a Procore record to ignore.",
            });
            return;
        }

        if (system1Selection.values.integrated.toLowerCase() === "yes") {
            newAlert({
                message:
                    "Record must be unlinked and not marked as 'Yes' in the 'Integrated' column first to ignore.",
            });
            return;
        }

        const action = matchTool.actions.find(
            (a: any) => a.method_type === "ignore"
        );

        if (!action) {
            newAlert({ message: "Error ignoring records" });
            console.log("Ignore method info not found in match tools data");
            return;
        }

        const method = action.method_name;
        const args = getArgsForSelectedRowFromActionParameters(action);
        const url = jobQueueUrl(method, args);

        const response = await post(url);

        console.log(`Executing ${url}...`);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Ignore response: ", data);

        if (response.ok && previewMode) {
            alertPreviewSubmitted("Ignore");
        } else if (response.ok) {
            updateStateForIgnoreOptimistically(system1Selection);

            setSystem1Selection(null);

            newAlert({ message: "Ignore request submitted", color: "success" });
        } else {
            newAlert({ message: "Ignore request failed", color: "danger" });
        }
    }

    function updateStateForIgnoreOptimistically(system1Selection: any) {
        const newSystem1Selection = { ...system1Selection.original };
        newSystem1Selection.integrated = "Ignore";

        const newData = { ...matchToolData };

        const newSystem1Data = newData.data_set_1.data.map((row: any) => {
            if (row.item_id === newSystem1Selection.item_id) {
                return newSystem1Selection;
            }
            return row;
        });

        newData.data_set_1.data = newSystem1Data;

        onSetMatchToolData(newData);
    }

    const getSystemDisplayName = (system: string) => {
        if (system === "psft") {
            return "PeopleSoft";
        }

        return system.charAt(0).toUpperCase() + system.slice(1);
    };

    const getColumnDisplayName = (key: string) => {
        const words = key.split("_");
        const capitalizedWords = words.map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1)
        );
        const newKey = capitalizedWords.join(" ");
        return newKey;
    };

    const toolActionExists = (action: string): boolean => {
        return matchTool.actions.some((obj: any) => obj.method_type === action);
    };

    // Opt-in per tool: the payload's hidden_columns lists keys to keep in row data
    // (Link/Unlink/Ignore read them from row.original) but not render as columns.
    // Tools that don't send it render every key, exactly as before.
    const hiddenColumns: string[] = matchToolData.hidden_columns || [];

    // Map table columns from JSON object keys so it works with any system/data.
    // Visible columns first; when "Show hidden fields" is on, append the hidden
    // ones at the end (never interleaved) so the useful columns keep their place.
    const buildColumns = (row: any) => {
        const keys = Object.keys(row || {});
        const ordered = keys.filter((key) => !hiddenColumns.includes(key));
        if (showHiddenColumns) {
            ordered.push(...keys.filter((key) => hiddenColumns.includes(key)));
        }
        return ordered.map((key) => ({
            Header: getColumnDisplayName(key),
            accessor: key,
            Cell: processCell,
        }));
    };

    const system1Columns = buildColumns(matchToolData.data_set_1.data[0]);
    const system2Columns = buildColumns(matchToolData.data_set_2.data[0]);

    function processCell({ value }: { value: any }) {
        if (Array.isArray(value)) {
            return value.join(", ");
        } else if (typeof value === "object") {
            return JSON.stringify(value);
        } else if (typeof value === "boolean") {
            return value ? "Yes" : "No";
        } else {
            return processCellText(value);
        }
    }

    function processCellText(text: string) {
        if (typeof text !== "string") return text;

        const withIsoStringFormatted = formatIsoStrings(text);
        const withClickableLinks = makeClickableLinkIfAny(
            withIsoStringFormatted
        );
        return withClickableLinks;
    }

    function makeClickableLinkIfAny(text: string) {
        if (typeof text !== "string") return text;

        const aHtmlTagRegex = /<a\s+([^>]+)>([^<]+)<\/a>/gi;

        // Sanitize the text since we are using dangerouslySetInnerHTML below
        const sanitizedText = DOMPurify.sanitize(text);

        const newText = sanitizedText.replace(
            aHtmlTagRegex,
            (match, attributes, innerText) => {
                // If target already exists, simply return the original match
                if (/target=/.test(attributes)) return match;

                // Add target="_blank" at the beginning of the attributes
                const modifiedAttributes = `${attributes} target='_blank' `;

                return `<a ${modifiedAttributes}>${innerText}</a>`;
            }
        );

        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: newText,
                }}
            />
        );
    }

    const getIgnoreButtonText = () => {
        if (
            !system2Selection &&
            system1Selection?.values.integrated === "Ignore"
        ) {
            return "Unignore";
        }
        return "Ignore";
    };

    function refreshMatchToolData() {
        let url = "";
        if (toolActionExists("select") && selection)
        {
            const params = matchTool.parameters;
            const args = params.map((p) => selection[p]).join("/");
            url = `/api/match_tools/tool_data/${tool}/${args}`;
            if (url.endsWith("/")) {
                url = url.slice(0, -1);
            }
        } else {
            url = `/api/match_tools/tool_data/${tool}`;
        }
        getMatchToolData(url);
    }

    // paddingTop: 0 overrides ReactTable.module.css's .tableBox top padding. That padding sits
    // INSIDE the scrollport (overflow:auto doesn't clip at the padding edge), so with the default
    // 10px top padding a scrolled-past row was still visibly rendered in that band above the
    // sticky header (THeadContent.module.css) before scrolling fully behind it. Zeroing it here
    // (scoped to this 72vh grid only — every other ReactTable usage keeps the normal padding)
    // removes the gap the row could peek through.
    const customStyle = { height: "72vh", overflowY: "auto", paddingTop: 0 };

    const table1Props = {
        data: matchToolData.data_set_1.data,
        // Always false because loading is handled by the parent component instead of in 2 tables here
        isLoading: false,
        columns: system1Columns,
        customStyle,
        onSetSelectedRow: onSetSystem1Selection,
        showPagination: true,
    };

    const table2Props = {
        data: matchToolData.data_set_2.data,
        isLoading: false,
        columns: system2Columns,
        customStyle,
        onSetSelectedRow: onSetSystem2Selection,
        showPagination: true,
    };

    return (
        <>
            {/* Header & buttons */}
            <div className={styles.headerAndBtnsBox}>
                <h1>{matchTool.description}</h1>

                {selection && (
                    <div>
                        <div>
                            <b>Project Number:</b> {selection.project_number}
                        </div>
                        <div>
                            <b>Name:</b> {selection.name}
                        </div>
                    </div>
                )}

                <span className={styles.btnsBox}>
                    <label title="Every button below queues a preview run: shows what it would do, writes nothing">
                        <input
                            type="checkbox"
                            checked={previewMode}
                            onChange={(e) => setPreviewMode(e.target.checked)}
                        />{" "}
                        Preview mode
                    </label>
                    {/* Send Button - creates the selected record in the other system.
                        Only rendered when the tool reports a "send" action. The portal gates it per
                        client (Controllers/Tools/MatchTools.FilterConfigGatedActions): most tools
                        drop it, Projects and Companies/Vendors send it disabled, which renders here
                        greyed out with the reason as the tooltip. */}
                    {toolActionExists("send") && (
                        <MatchActionButton
                            label={`Send to ${system2Label}`}
                            tooltip={sendTooltip}
                            fromIcon={system1Icon}
                            toIcon={system2Icon}
                            enabled={canSend}
                            onRun={sendRecord}
                        />
                    )}

                    {/* Get Button - creates the selected system-2 record in system 1. Mirror of
                        Send, and gated the same way: dropped on most tools, sent disabled on
                        Projects and Companies/Vendors (Controllers/Tools/MatchTools.FilterConfigGatedActions). */}
                    {toolActionExists("get") && (
                        <MatchActionButton
                            label={`Get from ${system2Label}`}
                            tooltip={getTooltip}
                            fromIcon={system2Icon}
                            toIcon={system1Icon}
                            enabled={canGet}
                            onRun={getRecord}
                        />
                    )}

                    {/* Link Button - only include if it is in the tool actions */}
                    {toolActionExists("link") && (
                        <Button color="primary" onClick={linkRecords}>
                            Link
                        </Button>
                    )}

                    {/* Unlink Button */}
                    {toolActionExists("unlink") && (
                        <Button color="danger" onClick={unlinkRecord}>
                            Unlink
                        </Button>
                    )}

                    {/* Ignore button */}
                    {toolActionExists("ignore") && (
                        <Button
                            className={styles.ignoreButton}
                            color="info"
                            onClick={handleIgnoreButtonClick}
                        >
                            {getIgnoreButtonText()}
                        </Button>
                    )}

                    {/* Change selection button */}
                    {toolActionExists("select") && (
                        <Button
                            color="secondary"
                            onClick={() => onSetModal(true)}
                        >
                            Change Selection
                        </Button>
                    )}
                    <Button color="secondary" onClick={refreshMatchToolData}>
                        Refresh data
                    </Button>
                    {hiddenColumns.length > 0 && (
                        <Button
                            color="secondary"
                            outline
                            onClick={() =>
                                setShowHiddenColumns(!showHiddenColumns)
                            }
                        >
                            {showHiddenColumns ? "Hide" : "Show"} hidden fields
                        </Button>
                    )}
                </span>
            </div>

            {/* Systems tables */}
            <Row>
                <Col lg={6} className={styles.table2Box}>
                    <h3 className={styles.tableName}>
                        {getSystemDisplayName(system2)}
                    </h3>
                    <div className={styles.table2}>
                        <ReactTable {...table2Props} />
                    </div>
                </Col>
                <Col lg={6} className={styles.table1Box}>
                    <h3 className={styles.tableName}>
                        {getSystemDisplayName(system1)}
                    </h3>
                    <div className={styles.table1}>
                        <ReactTable {...table1Props} />
                    </div>
                </Col>
            </Row>
        </>
    );
}

/**
 * Send / Get drawn the way the Launch page draws an integration (IntegrationRow tile): the source ->
 * target system logos beside the label. Preview stays on the Preview mode checkbox. The how-to
 * tooltip sits on a wrapping span as well, because Bootstrap gives a disabled .btn
 * pointer-events: none, so its own title never shows while it is disabled.
 */
function MatchActionButton({
    label,
    tooltip,
    fromIcon,
    toIcon,
    enabled,
    onRun,
}: {
    label: string;
    tooltip: string;
    fromIcon: string | undefined;
    toIcon: string | undefined;
    enabled: boolean;
    onRun: () => void;
}) {
    return (
        <span className="d-inline-block" title={tooltip}>
            <Button
                color="link"
                className={styles.actionButton}
                onClick={onRun}
                disabled={!enabled}
                title={tooltip}
            >
                {/* A system with no logo (COINS has none) still gets the direction glyph, as the
                    Launch tile falls back to its direction icon. */}
                <span className={styles.actionIcons}>
                    {fromIcon && toIcon && <img src={fromIcon} alt="" className={styles.systemIcon} />}
                    <FontAwesomeIcon icon={faArrowRight} />
                    {fromIcon && toIcon && <img src={toIcon} alt="" className={styles.systemIcon} />}
                </span>
                {label}
            </Button>
        </span>
    );
}
